import { useQuery } from "@tanstack/react-query";
import { Info, TriangleAlert } from "lucide-react";
import { useHistory } from "react-router";

import {
  Alert,
  AlertAction,
  AlertDescription,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchOrganisation } from "data/queries-common";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";

export const BillingInfoCard = () => {
  const history = useHistory();
  const [identity] = useIdentity();
  const identityId = resolveIdentityId(identity);

  const organisationQuery = useQuery({
    queryKey: [QueryKeys.organisation, identityId],
    queryFn: () => fetchOrganisation(),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing Details</CardTitle>
        <CardDescription>
          Contact information used on invoices and receipts.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {organisationQuery.isError && (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>
              An error occurred while fetching the billing details.
            </AlertDescription>
          </Alert>
        )}

        {organisationQuery.isPending && <Skeleton className="h-40 w-full" />}

        {organisationQuery.isSuccess && (
          <>
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-muted-foreground text-sm">
                  Organisation Name
                </p>
                <p className="text-sm">{organisationQuery.data.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Billing Email</p>
                <p className="text-sm">{organisationQuery.data.email}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Address</p>
                <p className="text-sm">
                  {organisationQuery.data.address || "-"}
                </p>
              </div>
            </div>

            <Alert variant="info">
              <Info />
              <AlertDescription>
                Billing details are the same as your general settings.
              </AlertDescription>
              <AlertAction>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => history.push(Routes.orgGeneralSettings)}
                >
                  Settings
                </Button>
              </AlertAction>
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
};
